import React, { useState, useEffect } from 'react';
import { X, ArrowRight, ArrowLeft, CheckCircle } from 'lucide-react';

import custom_api_func from './axios_api_func';
import { StepDots, styles } from './surveyShared';

/*
=====================================================================================
UserOnboardingSurvey
=====================================================================================
A two-step modal asked ONCE EVER per learner:
  1. "How did you find us?"
  2. "What should we teach next?" (future topics)

It is tracked in localStorage AND confirmed against the backend so it still
only asks once even if storage is cleared or the learner switches device.
If the backend confirms it's already been completed, this component renders
nothing (returns null) and calls onClose immediately.

HOW TO USE
---------------------------------------------------------------------------------
Render it independently of course completion (e.g. once on the first course
completion, or on first login) - it self-guards so it's safe to mount
speculatively:

  {showOnboardingSurvey && (
    <UserOnboardingSurvey
      academyid={academyid}
      csrftoken={csrftoken}
      onClose={() => setShowOnboardingSurvey(false)}
    />
  )}

EXPECTED BACKEND ENDPOINTS
---------------------------------------------------------------------------------
  GET  api/check_onboarding_survey2/{academyid}/
        -> { completed: boolean }

  POST api/submit_onboarding_survey2/{academyid}/   (multipart/form-data)
        fields: source, source_other, topics (JSON string), topics_other
        -> { success: true }
=====================================================================================
*/

const TOPIC_OPTIONS = [
  'Customer Onboarding',
  'Retention Strategies',
  'Upselling & Expansion',
  'Data & Analytics',
  'Communication Skills',
  'Product Knowledge',
  'Renewal Management',
  'Escalation Handling',
  'Automation Tools',
  'Leadership & Team Management',
];

const SOURCE_OPTIONS = [
  'Google Search',
  'LinkedIn',
  'Referral from a colleague or friend',
  'Social Media (Twitter / Instagram / etc.)',
  'Blog or Article',
  'Email Newsletter',
  'Other',
];

const ONBOARDING_SURVEY_STORAGE_KEY = 'sg_academy_onboarding_survey_done';

const STEPS = [
  { key: 'source', label: 'About You' },
  { key: 'topics', label: 'Future Topics' },
];

const UserOnboardingSurvey = ({ academyid, csrftoken, onClose, onSubmitted }) => {
  const [isMobile] = useState(window.innerWidth <= 640);

  // ---- whether this learner still needs to take the survey ----
  const [needsSurvey, setNeedsSurvey] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (localStorage.getItem(ONBOARDING_SURVEY_STORAGE_KEY)) {
      setNeedsSurvey(false);
      setChecking(false);
      return;
    }
    custom_api_func
      .get(`api/check_onboarding_survey2/${academyid}/`)
      .then((response) => {
        const completed = !!response?.data?.completed;
        if (completed) {
          localStorage.setItem(ONBOARDING_SURVEY_STORAGE_KEY, '1');
        }
        setNeedsSurvey(!completed);
      })
      .catch(() => {
        // If we can't confirm with the backend, fail open and ask - better
        // to occasionally ask twice than to never ask at all.
        setNeedsSurvey(true);
      })
      .finally(() => setChecking(false));
  }, [academyid]);

  // ---- form state ----
  const [stepIndex, setStepIndex] = useState(0);
  const [sourceValue, setSourceValue] = useState('');
  const [sourceOther, setSourceOther] = useState('');
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [topicsOther, setTopicsOther] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const toggleTopic = (topic) => {
    setSelectedTopics((prev) =>
      prev.includes(topic) ? prev.filter((t) => t !== topic) : [...prev, topic]
    );
  };

  if (checking) {
    // Still resolving whether to ask - render nothing but keep the parent's
    // rendering decision stable (don't pop the modal open/closed).
    return null;
  }

  if (!needsSurvey) {
    // Already completed - nothing to show. Let the parent know so it can
    // stop trying to render this component.
    if (onClose) onClose();
    return null;
  }

  const activeStep = STEPS[stepIndex].key;
  const isFinalStep = stepIndex === STEPS.length - 1;

  const canProceedFromSource = sourceValue !== '';
  const canProceedFromTopics = selectedTopics.length > 0 || topicsOther.trim().length > 0;

  const nextDisabled =
    (activeStep === 'source' && !canProceedFromSource) ||
    (activeStep === 'topics' && !canProceedFromTopics);

  const submitSurvey = () => {
    setSubmitting(true);
    setSubmitError(false);

    const formData = new FormData();
    formData.append('source', sourceValue);
    formData.append('source_other', sourceValue === 'Other' ? sourceOther : '');
    formData.append('topics', JSON.stringify(selectedTopics));
    formData.append('topics_other', topicsOther);

    custom_api_func
      .post(`api/submit_onboarding_survey2/${academyid}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-CSRFToken': csrftoken,
        },
      })
      .then(() => {
        localStorage.setItem(ONBOARDING_SURVEY_STORAGE_KEY, '1');
        setHasSubmitted(true);
        if (onSubmitted) onSubmitted();
      })
      .catch(() => {
        // Even if the save fails, don't trap the learner in the modal.
        setHasSubmitted(true);
        setSubmitError(true);
      })
      .finally(() => setSubmitting(false));
  };

  const goNext = () => {
    if (isFinalStep) {
      submitSurvey();
      return;
    }
    setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
  };
  const goBack = () => setStepIndex((i) => Math.max(i - 1, 0));

  return (
    <div style={styles.overlay}>
      <div style={{ ...styles.modal, maxWidth: isMobile ? '100%' : 520 }}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <div style={styles.headerEyebrow}>Quick Intro</div>
            <h2 style={styles.headerTitle}>Help us get to know you</h2>
          </div>
          <button style={styles.closeBtn} onClick={onClose} aria-label="Close">
            <X size={20} color="#5c5d70" />
          </button>
        </div>

        {!hasSubmitted && <StepDots steps={STEPS} activeIndex={stepIndex} />}

        {/* Body */}
        <div style={styles.body}>
          {hasSubmitted ? (
            <div>
              <h3 style={styles.stepTitle}>Thanks - that helps a lot! 🙌</h3>
              {submitError && (
                <div style={styles.inlineNotice}>
                  We couldn't save your answers just now, but that's on us - no
                  need to redo anything.
                </div>
              )}
            </div>
          ) : (
            <>
              {activeStep === 'source' && (
                <div>
                  <h3 style={styles.stepTitle}>One quick thing - how did you find us?</h3>
                  <p style={styles.stepSubtitle}>
                    We only ask this once. It helps us understand what's working.
                  </p>
                  <div style={styles.optionsList}>
                    {SOURCE_OPTIONS.map((option) => (
                      <label
                        key={option}
                        style={{
                          ...styles.radioOption,
                          ...(sourceValue === option ? styles.radioOptionSelected : {}),
                        }}
                      >
                        <input
                          type="radio"
                          name="source"
                          value={option}
                          checked={sourceValue === option}
                          onChange={() => setSourceValue(option)}
                          style={styles.radioInput}
                        />
                        <span>{option}</span>
                      </label>
                    ))}
                  </div>
                  {sourceValue === 'Other' && (
                    <input
                      type="text"
                      value={sourceOther}
                      onChange={(e) => setSourceOther(e.target.value)}
                      placeholder="Tell us where..."
                      style={styles.textInput}
                    />
                  )}
                </div>
              )}

              {activeStep === 'topics' && (
                <div>
                  <h3 style={styles.stepTitle}>What should we teach next?</h3>
                  <p style={styles.stepSubtitle}>
                    Pick as many topics as you like - this shapes our course roadmap.
                  </p>
                  <div style={styles.topicsGrid}>
                    {TOPIC_OPTIONS.map((topic) => {
                      const selected = selectedTopics.includes(topic);
                      return (
                        <button
                          key={topic}
                          type="button"
                          onClick={() => toggleTopic(topic)}
                          style={{
                            ...styles.topicChip,
                            ...(selected ? styles.topicChipSelected : {}),
                          }}
                        >
                          {selected && <CheckCircle size={14} style={{ marginRight: 6 }} />}
                          {topic}
                        </button>
                      );
                    })}
                  </div>
                  <input
                    type="text"
                    value={topicsOther}
                    onChange={(e) => setTopicsOther(e.target.value)}
                    placeholder="Something else you'd like covered? (optional)"
                    style={{ ...styles.textInput, marginTop: 16 }}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer nav */}
        <div style={styles.footer}>
          {!hasSubmitted ? (
            <>
              <button
                style={{ ...styles.navBtn, visibility: stepIndex === 0 ? 'hidden' : 'visible' }}
                onClick={goBack}
                disabled={submitting}
              >
                <ArrowLeft size={16} /> Back
              </button>
              <button
                style={{
                  ...styles.primaryBtn,
                  ...(nextDisabled ? styles.primaryBtnDisabled : {}),
                }}
                onClick={goNext}
                disabled={nextDisabled || submitting}
              >
                {submitting ? (
                  'Saving...'
                ) : isFinalStep ? (
                  <>
                    Submit <ArrowRight size={16} />
                  </>
                ) : (
                  <>
                    Continue <ArrowRight size={16} />
                  </>
                )}
              </button>
            </>
          ) : (
            <button style={styles.primaryBtn} onClick={onClose}>
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserOnboardingSurvey;